import {
  ChangeEvent,
  ClipboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useNumberFormat } from 'shared/hooks/use-number-format';
import { isNumber } from 'shared/utils/is-number';
import { parseFlexibleNumber } from 'shared/utils/parse-flexible-number';

type UseFormattedNumericInputProps = {
  value: number;
  onChange: (value: number) => void;
};

export const useFormattedNumericInput = ({
  value,
  onChange,
}: UseFormattedNumericInputProps) => {
  const separators = useNumberFormat();
  const [formattedValue, setFormattedValue] = useState('');

  const formattedValueRef = useRef(formattedValue);
  useEffect(() => {
    formattedValueRef.current = formattedValue;
  }, [formattedValue]);

  // Sync formattedValue when external value changes
  useEffect(() => {
    // If value is empty/invalid, clear the input
    if (!isNumber(value)) {
      if (formattedValueRef.current !== '') {
        setFormattedValue('');
      }
      return;
    }

    // Check if current local value matches the incoming value to avoid cursor jumps/overwrites
    const currentNumericValue = (() => {
      if (!formattedValueRef.current) {
        return NaN;
      }
      // Use the same parsing logic as the input handler
      const groupRegex = new RegExp(`\\${separators.group}`, 'g');
      const numericString = formattedValueRef.current
        .replace(groupRegex, '')
        .replace(separators.decimal, '.');
      return parseFloat(numericString);
    })();

    if (currentNumericValue === value) {
      return;
    }

    // Convert number to string with dot for processing
    const stringValue = value.toString();
    const [integerPart, decimalPart] = stringValue.split('.');

    let finalValue = integerPart.replace(
      /\B(?=(\d{3})+(?!\d))/g,
      separators.group,
    );
    if (decimalPart !== undefined) {
      finalValue += separators.decimal + decimalPart;
    }
    setFormattedValue(finalValue);
  }, [value, separators]);

  const onInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;

      // Allow digits, decimal separator, and group separator
      // We only strip truly invalid characters (letters, etc.)
      const validCharsRegex = new RegExp(
        `[^\\d\\${separators.decimal}\\${separators.group}]`,
        'g',
      );
      const cleanedValue = inputValue.replace(validCharsRegex, '');

      // Prevent multiple decimal separators
      const parts = cleanedValue.split(separators.decimal);
      let finalValue = cleanedValue;
      if (parts.length > 2) {
        finalValue = parts[0] + separators.decimal + parts.slice(1).join('');
      }

      setFormattedValue(finalValue);

      // Update the state with the numeric value
      const groupRegex = new RegExp(`\\${separators.group}`, 'g');
      const numericString = finalValue
        .replace(groupRegex, '')
        .replace(separators.decimal, '.');

      const numberValue = parseFloat(numericString);

      onChange(!isNaN(numberValue) ? numberValue : ('' as unknown as number));
    },
    [onChange, separators.decimal, separators.group],
  );

  const onInputBlur = useCallback(() => {
    if (isNumber(value)) {
      const stringValue = value.toString();
      const [integerPart, decimalPart] = stringValue.split('.');

      let finalValue = integerPart.replace(
        /\B(?=(\d{3})+(?!\d))/g,
        separators.group,
      );
      if (decimalPart !== undefined) {
        finalValue += separators.decimal + decimalPart;
      }
      setFormattedValue(finalValue);
    }
  }, [value, separators.group, separators.decimal]);

  const onInputPaste = useCallback(
    (e: ClipboardEvent<HTMLInputElement>) => {
      const pastedText = e.clipboardData.getData('text');
      if (!pastedText) {
        return;
      }

      const parsedNumber = parseFlexibleNumber(pastedText);

      if (!isNaN(parsedNumber)) {
        e.preventDefault();
        onChange(parsedNumber);
        // The useEffect will handle the formatting update based on value
      }
    },
    [onChange],
  );

  return {
    formattedValue,
    onInputChange,
    onInputBlur,
    onInputPaste,
  };
};
